import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EMPTY } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { GroupsBackend } from '@libs/src/groups/groups.backend';
import { ToastService } from '@libs/src/toast/toast.service';

import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import {
  Account,
  AccountGroupMembership,
} from '@libs/src/models/account.model';
import { Group } from '@libs/src/models/group.model';
import { GroupCustomSegment } from '@libs/src/models/group.model';
import {
  buildFormFromSegmentSelections,
  updateSegmentSelections,
} from '@libs/src/segment-selections/segment-selections.helper';
import { getMembership } from '@main-client/src/app/account/account.helpers';

import castArray from 'lodash-es/castArray';
import find from 'lodash-es/find';
import pickBy from 'lodash-es/pickBy';
import reduce from 'lodash-es/reduce';

@Component({
  selector: 'app-group-membership-dialog',
  templateUrl: './group-membership-dialog.component.html',
})
export class GroupMembershipDialogComponent implements OnInit {
  membership: AccountGroupMembership;
  membershipForm: UntypedFormGroup;
  isLoading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      account: Account;
      group: Group;
      onUpdate: (account: Account) => any;
    },
    public dialogRef: MatDialogRef<GroupMembershipDialogComponent>,
    public fb: UntypedFormBuilder,
    public groupsBackend: GroupsBackend,
    public toastService: ToastService,
  ) {}

  ngOnInit() {
    this.membership = getMembership(
      this.data.account.groups,
      this.data.group._id,
    );
    if (!this.membership) {
      return;
    }
    this.buildMembershipForm(this.membership);
  }

  saveMembership(account: Account, group: Group, form: UntypedFormGroup) {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    const update = {
      ...form.value,
      segment_selections: reduce(
        pickBy(form.value.segment_selections, (selection) => selection),
        (result, selectedNodes, segmentId) => {
          const customSegment = find(group.custom_segments, { _id: segmentId });
          return updateSegmentSelections(
            result,
            customSegment,
            castArray(selectedNodes),
          );
        },
        this.membership.segment_selections,
      ),
    };
    this.groupsBackend
      .putAccountsGroups(this.data.group, this.data.account, update)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.toastService.showError(getServerError(error));
          this.isLoading = false;
          return EMPTY;
        }),
        tap((response: AccountGroupMembership[]) => {
          this.membership = getMembership(response, this.data.group._id);
          const updatedAccount = { ...this.data.account, groups: response };
          if (this.data.onUpdate) {
            this.data.onUpdate(updatedAccount);
          }
          this.dialogRef.close({ account: updatedAccount });
        }),
      )
      .subscribe();
  }

  segmentSelectionsChanged(
    form: UntypedFormGroup,
    segment: GroupCustomSegment,
    selectedNodes: [string],
  ) {
    this.membershipForm.patchValue({
      segment_selections: {
        ...form.value.segment_selections,
        [segment._id]: selectedNodes,
      },
    });
  }

  buildMembershipForm(membership: AccountGroupMembership) {
    this.membershipForm = this.fb.group({
      inviteable: [membership.inviteable],
      opted_into_matchmaking: [membership.opted_into_matchmaking],
      role: [membership.role],
      searchable: [membership.searchable],
      expert_status: [membership.expert_status],
      segment_selections: buildFormFromSegmentSelections(
        this.data.group.custom_segments,
        this.membership.segment_selections,
      ),
    });
  }
}
